import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Textarea,
  Field,
  Spinner,
  makeStyles,
  tokens,
  Text,
} from '@fluentui/react-components';
import {
  WarningRegular,
  CheckmarkCircleRegular,
  ErrorCircleRegular,
} from '@fluentui/react-icons';
import { useDisconnectSession, useLogOffSession } from '@/hooks/useSessionQueries';
import { useNavigate } from '@tanstack/react-router';

const useStyles = makeStyles({
  dialog: {
    maxWidth: '500px',
  },
  warningSection: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorPaletteYellowBackground2,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: tokens.spacingVerticalL,
  },
  successSection: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorPaletteGreenBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  errorSection: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorPaletteRedBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
});

const disconnectSchema = z.object({
  reason: z.string().optional(),
});

type DisconnectFormData = z.infer<typeof disconnectSchema>;

interface DisconnectDialogProps {
  sessionId: string;
  sessionName: string;
  action: 'disconnect' | 'logoff';
  onClose: () => void;
}

export const DisconnectDialog: React.FC<DisconnectDialogProps> = ({
  sessionId,
  sessionName,
  action,
  onClose,
}) => {
  const styles = useStyles();
  const navigate = useNavigate();
  
  const disconnectMutation = useDisconnectSession();
  const logOffMutation = useLogOffSession();
  
  const mutation = action === 'disconnect' ? disconnectMutation : logOffMutation;
  
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<DisconnectFormData>({
    resolver: zodResolver(disconnectSchema),
    defaultValues: {
      reason: '',
    },
  });

  const onSubmit = async (data: DisconnectFormData) => {
    try {
      if (action === 'disconnect') {
        await disconnectMutation.mutateAsync({
          sessionId,
          request: { reason: data.reason },
        });
      } else {
        await logOffMutation.mutateAsync({
          sessionId,
          request: { reason: data.reason },
        });
      }

      // Close dialog and navigate back to sessions list after short delay
      setTimeout(() => {
        onClose();
        navigate({ to: '/sessions' });
      }, 2000);
    } catch {
      // Error is handled by mutation state
    }
  };

  const actionTitle = action === 'disconnect' ? 'Disconnect Session' : 'Log Off Session';
  const actionVerb = action === 'disconnect' ? 'disconnect' : 'log off';
  const actionWarning = action === 'disconnect' 
    ? 'This will disconnect your session but keep it running. You can reconnect later.'
    : 'This will completely log off your session. All unsaved work will be lost.';

  return (
    <Dialog open onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className={styles.dialog}>
        <DialogBody>
          <DialogTitle>{actionTitle}</DialogTitle>
          <DialogContent>
            {mutation.isSuccess ? (
              <div className={styles.successSection}>
                <CheckmarkCircleRegular style={{ fontSize: '24px', color: tokens.colorPaletteGreenForeground1 }} />
                <div>
                  <Text weight="semibold">Success!</Text>
                  <br />
                  <Text size={200}>
                    Session has been {action === 'disconnect' ? 'disconnected' : 'logged off'} successfully.
                  </Text>
                </div>
              </div>
            ) : mutation.isError ? (
              <>
                <div className={styles.errorSection}>
                  <ErrorCircleRegular style={{ fontSize: '24px', color: tokens.colorPaletteRedForeground1 }} />
                  <div>
                    <Text weight="semibold">Error</Text>
                    <br />
                    <Text size={200}>
                      Failed to {actionVerb} the session. {mutation.error?.message}
                    </Text>
                  </div>
                </div>
                <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
                  <Text>Would you like to try again?</Text>
                </form>
              </>
            ) : (
              <>
                <div className={styles.warningSection}>
                  <WarningRegular style={{ fontSize: '24px', color: tokens.colorPaletteYellowForeground1 }} />
                  <div>
                    <Text weight="semibold">Warning</Text>
                    <br />
                    <Text size={200}>{actionWarning}</Text>
                  </div>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
                  <Text>
                    Are you sure you want to {actionVerb} the session: <strong>{sessionName}</strong>?
                  </Text>

                  <Controller
                    name="reason"
                    control={control}
                    render={({ field }) => (
                      <Field label="Reason (optional)">
                        <Textarea
                          {...field}
                          placeholder="Enter a reason for this action..."
                          resize="vertical"
                          disabled={isSubmitting}
                        />
                      </Field>
                    )}
                  />
                </form>
              </>
            )}
          </DialogContent>
          <DialogActions>
            {mutation.isSuccess ? (
              <Button appearance="primary" onClick={onClose}>
                Close
              </Button>
            ) : (
              <>
                <DialogTrigger disableButtonEnhancement>
                  <Button appearance="secondary" disabled={isSubmitting}>
                    Cancel
                  </Button>
                </DialogTrigger>
                <Button 
                  appearance="primary" 
                  onClick={handleSubmit(onSubmit)}
                  disabled={isSubmitting}
                  icon={isSubmitting ? <Spinner size="tiny" /> : undefined}
                >
                  {isSubmitting ? 'Processing...' : `Confirm ${action === 'disconnect' ? 'Disconnect' : 'Log Off'}`}
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};